import { useEffect, useState } from 'react'
import { Box, Container, Heading, VStack, Text, Code, Button } from '@chakra-ui/react'
import { supabase } from '@/lib/supabase'

export function DebugAuthPage() {
  const [sessionInfo, setSessionInfo] = useState<any>(null)
  const [testResult, setTestResult] = useState<any>(null)

  const checkSession = async () => {
    console.log('🔍 Checking session...')

    // 1. Get session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()

    console.log('Session:', session)
    console.log('Session error:', sessionError)
    console.log('Access Token (full):', session?.access_token)
    console.log('Access Token length:', session?.access_token?.length)

    // Mask the token for security (show first 20 and last 20 chars)
    const maskedToken = session?.access_token
      ? `${session.access_token.substring(0, 20)}...${session.access_token.substring(session.access_token.length - 20)}`
      : null

    setSessionInfo({
      hasSession: !!session,
      userId: session?.user?.id,
      email: session?.user?.email,
      hasAccessToken: !!session?.access_token,
      accessToken: maskedToken, // Show masked token
      accessTokenLength: session?.access_token?.length,
      expiresAt: session?.expires_at,
      expiresIn: session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : null,
      error: sessionError,
    })

    // 2. Test a simple query to see if auth.uid() works
    const { data, error } = await supabase
      .from('users')
      .select('id, email, agency_id')
      .eq('id', session?.user?.id || '')
      .maybeSingle()

    console.log('Test query result:', { data, error })

    setTestResult({
      queryWorked: !error,
      data,
      error: error?.message,
    })
  }

  useEffect(() => {
    checkSession()
  }, [])

  const testInsert = async () => {
    console.log('🔍 Testing residence insert...')

    const { data: { session } } = await supabase.auth.getSession()

    if (!session) {
      alert('No session!')
      return
    }

    console.log('🔍 Session for insert:', {
      hasSession: !!session,
      userId: session.user.id,
      hasAccessToken: !!session.access_token,
      tokenLength: session.access_token?.length,
      tokenPreview: session.access_token?.substring(0, 30) + '...',
    })

    // Get user's agency_id
    const { data: userData } = await supabase
      .from('users')
      .select('agency_id')
      .eq('id', session.user.id)
      .single()

    console.log('🔍 User agency_id:', userData?.agency_id)

    const testResidence = {
      name: 'TEST RESIDENCE ' + new Date().toISOString(),
      residence_key: 'TEST-' + Date.now(),
      country: 'FR',
      address: 'Test Address',
      city: 'Test City',
      postal_code: '75001',
      agency_id: userData?.agency_id || '', // Changed: use empty string if no agency_id
    }

    console.log('🔍 Attempting insert with:', testResidence)
    console.log('🔍 Field types:', {
      name: typeof testResidence.name,
      residence_key: typeof testResidence.residence_key,
      country: typeof testResidence.country,
      address: typeof testResidence.address,
      city: typeof testResidence.city,
      postal_code: typeof testResidence.postal_code,
      agency_id: typeof testResidence.agency_id,
      agency_id_value: testResidence.agency_id,
      agency_id_is_null: testResidence.agency_id === null,
    })

    // IMPORTANT: Check network tab in DevTools to see if Authorization header is sent
    console.warn('⚠️ VÉRIFIEZ LE NETWORK TAB: Regardez si le header "Authorization: Bearer <token>" est présent dans la requête POST /rest/v1/residences')

    // Log the exact request that will be sent
    console.log('🔍 About to call supabase.from("residences").insert(...)')

    // Test WITHOUT .select() to see if that's the problem
    const { data, error } = await supabase
      .from('residences')
      .insert(testResidence)
      // .select()  // <-- Temporarily commented out
      // .single()

    console.log('🔍 Insert result:', { data, error })

    if (error) {
      console.error('🔍 Insert error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
      })
      alert('Error: ' + error.message + ' (code: ' + error.code + ')')
    } else if (data) {
      console.log('🔍 SUCCESS! Residence created:', (data as any).id)
      alert('Success! Residence created: ' + ((data as any).id || 'ID unknown'))
    } else {
      console.log('🔍 Insert completed but no data returned')
      alert('Insert may have succeeded but no data returned')
    }
  }

  return (
    <Box minH="100vh" bg="gray.50" py={12}>
      <Container maxW="container.lg">
        <VStack gap={6} align="stretch">
          <Heading>🔍 Debug Auth Session</Heading>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4}>Session Info</Heading>
            <Code display="block" whiteSpace="pre" p={4} borderRadius="md">
              {JSON.stringify(sessionInfo, null, 2)}
            </Code>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" mb={4}>Test Query Result</Heading>
            <Code display="block" whiteSpace="pre" p={4} borderRadius="md">
              {JSON.stringify(testResult, null, 2)}
            </Code>
          </Box>

          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <VStack gap={4}>
              <Button onClick={checkSession} colorScheme="blue" w="full">
                Refresh Session Info
              </Button>
              <Button onClick={testInsert} colorScheme="green" w="full">
                Test Residence Insert
              </Button>
            </VStack>
          </Box>

          <Box bg="yellow.50" p={4} borderRadius="lg" border="1px solid" borderColor="yellow.200">
            <Text fontWeight="bold" mb={2}>📝 Instructions:</Text>
            <VStack align="start" gap={1} fontSize="sm">
              <Text>1. Vérifiez que &quot;hasSession&quot; est true</Text>
              <Text>2. Vérifiez que &quot;userId&quot; n&apos;est pas null</Text>
              <Text>3. Vérifiez que &quot;accessToken&quot; a une longueur &gt; 0</Text>
              <Text>4. Si tout est OK mais l&apos;insert échoue avec 403, le problème est la policy RLS</Text>
              <Text>5. Si la session est null ou le token manque, le problème est l&apos;authentification</Text>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
